import { useState } from "react";
import { useIntl } from "react-intl";
import { useBudgetEdit, IUseBudgetEditOverrides } from "./hooks/useBudgetEdit";
import { useBudgetCanAct } from "./hooks/useBudgetCanAct";

import { IBudget } from "../../../models/budgetV2";

import { BudgetVehicleCard } from "../components/BudgetVehicleCard";
import { ServiceSkeleton } from "../components/ServiceSkeleton";
import { BudgetHeader } from "../components/BudgetHeader";
import { BudgetSummary } from "../components/BudgetSummary";
import { BudgetServiceForm } from "../components/budgetServiceForm";
import { CounterProposal } from "./components/CounterProposal";
import { BudgetEditActions } from "./components/BudgetEditActions";
import { BudgetAttachments } from "../components/budgetAttachments";

import "../index.css";

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
    <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);

interface IBudgetEditProps extends IUseBudgetEditOverrides {
    persona: string;
    onClose?: () => void;
    onBreadcrumbClick?: () => void;
}

const BudgetEdit = (props: IBudgetEditProps) => {
    const intl = useIntl();

    const { persona, onClose, onBreadcrumbClick, ...overrides } = props;

    const {
        budget, truckerPayPlan, loading, submitting, statusLabel, statusBadgeClass, approvedDateLabel,
        hasBudgetChanges,
        setBudget,
        setServices,
        removeService,
        getInitialService,
        addEmptyService,
        addEmptyItemToService,
        saveBudgetUpdate,
        acceptBudget,
        rejectBudget,
        counterProposeBudget,
        attachments,
        addAttachments,
        removeAttachment,
    } = useBudgetEdit(persona, overrides);

    const [counterProposalValue, setCounterProposalValue] = useState<number>(0);
    const [counterProposalObservation, setCounterProposalObservation] = useState<string>("");
    const canAct = useBudgetCanAct(budget, loading, persona);

    return (
        <div className="w-full">
            <BudgetHeader persona={persona} budget={budget} loading={loading} onClose={onClose} onBreadcrumbClick={onBreadcrumbClick} />

            {<BudgetVehicleCard budget={budget} loading={loading || !budget} />}

            {!loading && budget ? (
                <div className={`mb-6 px-6`}>
                    <div className={`font-semibold mb-4 text-size-18`}>{intl.formatMessage({ id: 'intl-msg:budgets.values' })}</div>
                    {(budget?.versionData?.services ?? []).map((service, index) => (
                        <BudgetServiceForm
                            key={service.id || `service-${index}`}
                            budget={budget}
                            truckerPayPlan={truckerPayPlan}
                            setService={(service) => {
                                setServices(budget.versionData?.services?.map((s, i) => i === index ? service : s) ?? []);
                            }}
                            onRemoveService={() => removeService(index)}
                            onAddItem={() => addEmptyItemToService(index)}
                            serviceIndex={index}
                            initialService={getInitialService(index)}
                        />
                    ))}
                    <button
                        type="button"
                        className="flex flex-row items-center text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-pointer mt-2"
                        onClick={addEmptyService}
                    >
                        <img
                            src="/assets/images/plus.svg"
                            alt={intl.formatMessage({ id: 'intl-msg:budgets.addService' })}
                            className="w-5 h-5 flex-shrink-0 mr-2"
                        />
                        <span>{intl.formatMessage({ id: 'intl-msg:budgets.addService' })}</span>
                    </button>
                </div>
            ) : <ServiceSkeleton />}

            {!loading ? <BudgetSummary persona={persona} budget={budget} truckerPayPlan={truckerPayPlan} /> : <SkeletonBlock className="h-[100px] w-full mb-4" />}

            {!loading && (
                <BudgetAttachments
                    files={attachments}
                    budgetId={budget?.uuid}
                    allowUpload={true}
                    onFilesAdd={addAttachments}
                    onFileRemove={removeAttachment}
                />
            )}

            <CounterProposal
                budget={budget}
                readOnly={!canAct || hasBudgetChanges}
                onValueChange={setCounterProposalValue}
                onObservationChange={setCounterProposalObservation}
            />

            <div className={`mb-6 px-6`}>
                <div className={`font-bold mb-4 flex flex-row items-center gap-2 justify-between`}>
                    <span
                        className={`min-w-60 px-3 py-2 rounded-full text-size-12 text-center inline-block whitespace-nowrap ${statusBadgeClass}`}
                    >
                        {statusLabel}
                    </span>
                    <span>{approvedDateLabel}</span>
                </div>
            </div>

            {canAct && (
                <BudgetEditActions
                    hasChanges={hasBudgetChanges}
                    submitting={submitting}
                    onSaveUpdate={saveBudgetUpdate}
                    onAccept={acceptBudget}
                    onReject={rejectBudget}
                    canCounterPropose={!hasBudgetChanges && counterProposalValue > 0}
                    onCounterPropose={() => counterProposeBudget(counterProposalValue, counterProposalObservation)}
                />
            )}
        </div>
    );
};

export default BudgetEdit;
